package diana.soleil.design.iraniancanadienbazzar.feature.addbusiness

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_back
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_next
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_add_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_brand_assets
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_cover_hint
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_cover_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_gallery_hint
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_gallery_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_logo_button
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_logo_hint
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_logo_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_preview_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_proof_button
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_proof_desc
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_proof_note
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_proof_title
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_remove_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_subtitle
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_title
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_verify_b1
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_verify_b2
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_verify_b3
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_verify_desc
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_photos_verify_title
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_save_draft
import org.jetbrains.compose.resources.decodeToImageBitmap
import org.jetbrains.compose.resources.stringResource

/**
 * Step 3 — Photos & Verification (Stitch `e8960694`). Cover/logo/gallery run the system image
 * picker and preview the picked bytes in place; the actual Storage upload happens at submit, once
 * the pending `businesses/{id}` doc exists (its ownerUid authorizes the Storage path). All photos
 * are optional. Proof-of-business upload remains part of the claim flow (see ClaimBusiness) —
 * the button here stays disabled.
 */
@Composable
fun AddBusinessPhotosScreen(
    draft: AddBusinessDraft,
    onPickCover: () -> Unit,
    onRemoveCover: () -> Unit,
    onPickLogo: () -> Unit,
    onRemoveLogo: () -> Unit,
    onAddGalleryPhoto: () -> Unit,
    onRemoveGalleryPhoto: (Int) -> Unit,
    onSaveDraft: () -> Unit,
    onBack: () -> Unit,
    onNext: () -> Unit,
    modifier: Modifier = Modifier,
) {
    WizardScaffold(
        stepIndex = 3,
        title = stringResource(Res.string.ab_photos_title),
        onBack = onBack,
        modifier = modifier,
        bottomBar = {
            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                AppButton(
                    text = stringResource(Res.string.ab_save_draft),
                    onClick = onSaveDraft,
                    modifier = Modifier.weight(1f),
                    variant = AppButtonVariant.Text,
                )
                AppButton(
                    text = stringResource(Res.string.ab_back),
                    onClick = onBack,
                    modifier = Modifier.weight(1f),
                    variant = AppButtonVariant.Secondary,
                )
                AppButton(
                    text = stringResource(Res.string.ab_next),
                    onClick = onNext,
                    modifier = Modifier.weight(1f),
                    trailingIcon = "arrow_forward",
                )
            }
        },
    ) {
        Text(
            text = stringResource(Res.string.ab_photos_subtitle),
            style = AppTheme.typography.bodySm,
            color = AppTheme.colors.onSurfaceVariant,
        )

        // Brand assets
        WizardCard {
            SectionHeading(stringResource(Res.string.ab_photos_brand_assets))

            SectionHeading(stringResource(Res.string.ab_photos_cover_label))
            PickableImageArea(
                image = draft.cover,
                emptyIcon = "add_a_photo",
                emptyLabel = stringResource(Res.string.ab_photos_cover_hint),
                onPick = onPickCover,
                onRemove = onRemoveCover,
                modifier = Modifier.fillMaxWidth().aspectRatio(16f / 9f),
            )

            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md), verticalAlignment = Alignment.CenterVertically) {
                PickableImageArea(
                    image = draft.logo,
                    emptyIcon = "cloud_upload",
                    emptyLabel = null,
                    onPick = onPickLogo,
                    onRemove = onRemoveLogo,
                    modifier = Modifier.size(AppTheme.sizes.avatarLg),
                )
                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs)) {
                    Text(stringResource(Res.string.ab_photos_logo_label), style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurface)
                    Text(stringResource(Res.string.ab_photos_logo_hint), style = AppTheme.typography.bodySm, color = AppTheme.colors.onSurfaceVariant)
                }
                AppButton(
                    text = stringResource(Res.string.ab_photos_logo_button),
                    onClick = onPickLogo,
                    variant = AppButtonVariant.Secondary,
                )
            }

            SectionHeading(stringResource(Res.string.ab_photos_gallery_label))
            Text(stringResource(Res.string.ab_photos_gallery_hint), style = AppTheme.typography.bodySm, color = AppTheme.colors.onSurfaceVariant)
            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                repeat(AddBusinessDraft.MAX_GALLERY_PHOTOS) { index ->
                    PickableImageArea(
                        image = draft.gallery.getOrNull(index),
                        emptyIcon = "add",
                        emptyLabel = null,
                        onPick = onAddGalleryPhoto,
                        onRemove = { onRemoveGalleryPhoto(index) },
                        modifier = Modifier.weight(1f).aspectRatio(1f),
                    )
                }
            }
        }

        // Verification value proposition
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.secondaryContainer, AppTheme.shapes.card)
                .padding(AppTheme.spacing.lg),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                AppIcon("verified_user", tint = AppTheme.colors.onSecondaryContainer)
                Text(stringResource(Res.string.ab_photos_verify_title), style = AppTheme.typography.labelMd, color = AppTheme.colors.onSecondaryContainer)
            }
            Text(stringResource(Res.string.ab_photos_verify_desc), style = AppTheme.typography.bodySm, color = AppTheme.colors.onSecondaryContainer)
            VerifyBenefit(stringResource(Res.string.ab_photos_verify_b1))
            VerifyBenefit(stringResource(Res.string.ab_photos_verify_b2))
            VerifyBenefit(stringResource(Res.string.ab_photos_verify_b3))
        }

        // Proof of business (lives in the claim flow; kept as the Stitch placeholder here)
        WizardCard {
            SectionHeading(stringResource(Res.string.ab_photos_proof_title))
            Text(stringResource(Res.string.ab_photos_proof_desc), style = AppTheme.typography.bodySm, color = AppTheme.colors.onSurfaceVariant)
            AppButton(
                text = stringResource(Res.string.ab_photos_proof_button),
                onClick = {},
                modifier = Modifier.fillMaxWidth(),
                variant = AppButtonVariant.Secondary,
                leadingIcon = "upload_file",
                enabled = false,
            )
            InfoNote(stringResource(Res.string.ab_photos_proof_note))
        }
    }
}

/**
 * An upload slot: empty → dashed-style bordered area that opens the system picker; picked → the
 * image previewed in place (decoded from the picked bytes) with a remove chip, tap to replace.
 */
@Composable
private fun PickableImageArea(
    image: PickedImage?,
    emptyIcon: String,
    emptyLabel: String?,
    onPick: () -> Unit,
    onRemove: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .clip(AppTheme.shapes.card)
            .border(AppTheme.sizes.borderWidth, AppTheme.colors.outlineVariant, AppTheme.shapes.card)
            .clickable(onClick = onPick),
        contentAlignment = Alignment.Center,
    ) {
        val bitmap: ImageBitmap? = remember(image) {
            image?.let { runCatching { it.bytes.decodeToImageBitmap() }.getOrNull() }
        }
        if (bitmap != null) {
            Image(
                bitmap = bitmap,
                contentDescription = stringResource(Res.string.ab_photos_preview_a11y),
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize(),
            )
            Box(
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(AppTheme.spacing.xs)
                    .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                    .clickable(onClick = onRemove)
                    .padding(AppTheme.spacing.xxs),
            ) {
                AppIcon(
                    name = "close",
                    size = AppTheme.sizes.iconSm,
                    tint = AppTheme.colors.onSurface,
                    contentDescription = stringResource(Res.string.ab_photos_remove_a11y),
                )
            }
        } else {
            Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                AppIcon(name = emptyIcon, tint = AppTheme.colors.onSurfaceVariant, contentDescription = stringResource(Res.string.ab_photos_add_a11y))
                if (emptyLabel != null) {
                    Text(
                        text = emptyLabel,
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.onSurfaceVariant,
                        modifier = Modifier.padding(top = AppTheme.spacing.xs),
                    )
                }
            }
        }
    }
}

@Composable
private fun VerifyBenefit(text: String) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
        AppIcon("check_circle", size = AppTheme.sizes.iconSm, tint = AppTheme.colors.onSecondaryContainer)
        Text(text = text, style = AppTheme.typography.bodySm, color = AppTheme.colors.onSecondaryContainer)
    }
}
